/**
 * Component: Product Video
 * ------------------------------------------------------------------------------
 * Component to control the video player in PDP.
 *
 * @namespace productVideo
 */
import cssClasses from '../helpers/cssClasses';
import { on } from '../helpers/utils';
/**
 * DOM selectors.
 */

const selectors = {
  container: '[js-product-video="container"]',
  pause: '[js-product-video="pause"]',
  play: '[js-product-video="play"]',
  video: '[js-product-video="video"]',
  muteToggle: '[js-product-video="muteToggle"]',
  mute: '[js-product-video="mute"]',
  unmute: '[js-product-video="unmute"]'
};
/**
 * Create a new example object.
 * @param {object} config - Settings object defined when instantiating.
 */

export default (() => {
  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    container: document.querySelector(selectors.container),
    video: document.querySelectorAll(selectors.video),
    playBtn: document.querySelectorAll(selectors.play),
    pauseBtn: document.querySelectorAll(selectors.pause),
    muteToggleBtn: document.querySelectorAll(selectors.muteToggle),
    muteIcon: document.querySelectorAll(selectors.mute),
    unmuteIcon: document.querySelectorAll(selectors.unmute)
  };
  /**
   * Initialise component.
   */

  function init() {
    if (!nodeSelectors.video) {
      return;
    }

    setEventListeners();
    setEventBusListeners();
  }
  /**
   * Set event listeners.
   */


  function setEventListeners() {
    nodeSelectors.playBtn.forEach((element, index) => {
      on('click', element, () => {
        handlePlayEvent(index);
      });
    });
    nodeSelectors.pauseBtn.forEach((element, index) => {
      on('click', element, () => {
        handlePauseEvent(index);
      });
    });
    nodeSelectors.muteToggleBtn.forEach((element, index) => {
      on('click', element, () => {
        handleMuteToggleEvent(index);
      });
    });
  }

  function setEventBusListeners() {
    Frame.EventBus.listen('VideoPlayer:pause', () => {
      pauseAllPlayers();
    });
  }
  /**
   * Pause All Players
   */


  function pauseAllPlayers() {
    nodeSelectors.video.forEach((element, index) => {
      element.pause();
      nodeSelectors.container.classList.remove('is-playing');
      nodeSelectors.pauseBtn[index].classList.add(cssClasses.hidden);
      nodeSelectors.muteToggleBtn[index].classList.add(cssClasses.hidden);
      nodeSelectors.playBtn[index].classList.remove(cssClasses.hidden);
      nodeSelectors.pauseBtn[index].setAttribute('tabindex', -1);
      nodeSelectors.muteToggleBtn[index].setAttribute('tabindex', -1);
      nodeSelectors.playBtn[index].setAttribute('tabindex', 0);
    });
  }
  /**
   * Play the background video and display pause controls.
   */


  function handlePlayEvent(index) {
    event.preventDefault();
    nodeSelectors.video[index].play();
    nodeSelectors.container.classList.add('is-playing');
    nodeSelectors.container.classList.remove('is-paused');
    showPauseControls(index);
  }
  /**
   * Pause the background video and display play controls.
   */


  function handlePauseEvent(index) {
    event.preventDefault();
    nodeSelectors.video[index].pause();
    nodeSelectors.container.classList.remove('is-playing');
    nodeSelectors.container.classList.add('is-paused');
    showPlayControls(index);
  }
  /**
   * Turn on/off the background video sound (mute/unmute).
   */


  function handleMuteToggleEvent(index) {
    var _nodeSelectors$video$;

    const isMuted = nodeSelectors === null || nodeSelectors === void 0 ? void 0 : (_nodeSelectors$video$ = nodeSelectors.video[index]) === null || _nodeSelectors$video$ === void 0 ? void 0 : _nodeSelectors$video$.muted;

    if (isMuted) {
      nodeSelectors.unmuteIcon[index].classList.add(cssClasses.hidden);
      nodeSelectors.muteIcon[index].classList.remove(cssClasses.hidden);
    } else {
      nodeSelectors.muteIcon[index].classList.add(cssClasses.hidden);
      nodeSelectors.unmuteIcon[index].classList.remove(cssClasses.hidden);
    }

    nodeSelectors.video[index].muted = !isMuted;
  }
  /**
   * Show the play button and hide the pause button.
   */


  function showPlayControls(index) {
    nodeSelectors.pauseBtn[index].classList.add(cssClasses.hidden);
    nodeSelectors.muteToggleBtn[index].classList.add(cssClasses.hidden);
    nodeSelectors.playBtn[index].classList.remove(cssClasses.hidden);
    nodeSelectors.pauseBtn[index].setAttribute('tabindex', -1);
    nodeSelectors.muteToggleBtn[index].setAttribute('tabindex', -1);
    nodeSelectors.playBtn[index].setAttribute('tabindex', 0);
    nodeSelectors.playBtn[index].focus();
  }
  /**
   * Show the pause button and hide the play button.
   */


  function showPauseControls(index) {
    nodeSelectors.pauseBtn[index].classList.remove(cssClasses.hidden);
    nodeSelectors.muteToggleBtn[index].classList.remove(cssClasses.hidden);
    nodeSelectors.playBtn[index].classList.add(cssClasses.hidden);
    nodeSelectors.pauseBtn[index].setAttribute('tabindex', 0);
    nodeSelectors.muteToggleBtn[index].setAttribute('tabindex', 0);
    nodeSelectors.playBtn[index].setAttribute('tabindex', -1);
    nodeSelectors.pauseBtn[index].focus();
  }
  /**
   * Expose public interface.
   */


  return Object.freeze({
    init
  });
});